import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useApi } from './ApiProvider';
import { formatMoney, getCurrencySymbol, moneyPrefix } from '../lib/currency';
import { ensureCatalogAccess } from '../lib/ensureCatalogAccess';

export interface StoreInfo {
  id?: string;
  shopDomain: string;
  name?: string | null;
  currency: string;
  timezone?: string;
  primaryLocale?: string;
}

interface StoreContextValue {
  store: StoreInfo | null;
  loading: boolean;
  currency: string;
  currencySymbol: string;
  /** Format amount in store currency */
  formatMoney: (amount: number, opts?: { maximumFractionDigits?: number }) => string;
  /** TextField prefix for money inputs */
  moneyPrefix: string;
  refresh: () => Promise<void>;
}

const StoreContext = createContext<StoreContextValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [store, setStore] = useState<StoreInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Currency is always Shopify shop currency from our store record (synced on install)
      const me = await api.get<{
        id?: string;
        shopDomain?: string;
        name?: string | null;
        currency?: string;
        timezone?: string;
        primaryLocale?: string;
      }>('/stores/me');
      const currency = (me.currency || sessionStorage.getItem('gf_currency') || '')
        .toUpperCase();
      if (currency) {
        try {
          sessionStorage.setItem('gf_currency', currency);
        } catch {
          /* ignore */
        }
      }
      setStore({
        id: me.id,
        shopDomain:
          me.shopDomain ||
          sessionStorage.getItem('gf_shop') ||
          'demo.myshopify.com',
        name: me.name,
        // Prefer Shopify shop currency from API — never invent USD
        currency: currency || 'INR',
        timezone: me.timezone,
        primaryLocale: me.primaryLocale,
      });
    } catch {
      let cached = '';
      let shop = 'demo.myshopify.com';
      try {
        cached = (sessionStorage.getItem('gf_currency') || '').toUpperCase();
        shop = sessionStorage.getItem('gf_shop') || shop;
      } catch {
        /* ignore */
      }
      setStore({
        shopDomain: shop,
        currency: cached || '',
        name: 'Store',
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void (async () => {
      let shop =
        new URLSearchParams(window.location.search).get('shop') || '';
      if (!shop) {
        try {
          shop = sessionStorage.getItem('gf_shop') || '';
        } catch {
          shop = '';
        }
      }
      // Silent link — no merchant "sync catalog" step
      if (shop) {
        await ensureCatalogAccess(shop);
      }
      await refresh();
    })();
  }, [refresh]);

  // Shop currency ISO code from Shopify (e.g. INR, USD). Empty until loaded.
  let currencyCached = '';
  try {
    currencyCached = sessionStorage.getItem('gf_currency') || '';
  } catch {
    /* ignore */
  }
  const currency = (store?.currency || currencyCached || '').toUpperCase();

  const value = useMemo<StoreContextValue>(
    () => ({
      store,
      loading,
      currency,
      currencySymbol: getCurrencySymbol(currency),
      formatMoney: (amount, opts) => formatMoney(amount, currency, opts),
      moneyPrefix: moneyPrefix(currency),
      refresh,
    }),
    [store, loading, currency, refresh],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}
